// payment-terms.ts — payment terms of a quote.
//
// Terms live on the client record and are copied onto the quote, where they stay editable.
// Pure and stateless so the input screen, the production table and the production gate all
// read the same rule. No pricing effect: nothing here touches a price, discount or margin.

import { engineSettings } from "../../data";

/**
 * The payment term codes. This union MIRRORS the check constraint on the clients / quotes `payment_delay`
 * column (migration 20260928120000_quote_settings.sql); adding a term means changing that constraint, this
 * type and the `quote_engine.payment_terms` setting together. Which terms carry a deposit is NOT here: it
 * is the `deposit` flag of the setting, read by {@link paymentTermCodes}.
 */
export type PaymentTermCode = "COMMANDE" | "J30" | "J45FDM" | "50_LIVRAISON" | "50_J30" | "50_J45FDM";

/** i18n key of each term label (the label reaches the client, French lives in fr.json). */
export const PAYMENT_TERM_LABEL_KEY: Readonly<Record<PaymentTermCode, string>> = {
  COMMANDE: "enginePaymentTermCommande",
  J30: "enginePaymentTermJ30",
  J45FDM: "enginePaymentTermJ45fdm",
  "50_LIVRAISON": "enginePaymentTerm50Livraison",
  "50_J30": "enginePaymentTerm50J30",
  "50_J45FDM": "enginePaymentTerm50J45fdm",
};

/** One term of the menu: its code, the key of its label and whether money is due at order time. */
export interface PaymentTermEntry {
  code: PaymentTermCode;
  labelKey: string;
  deposit: boolean;
}

/**
 * The payment terms in menu order, from the settings. `deposit` marks terms with money due at order time
 * and drives the payment alert; never infer it by matching label text.
 * @returns The terms.
 * @throws Error when a configured code is not one of the {@link PaymentTermCode} values.
 */
export function paymentTermCodes(): readonly PaymentTermEntry[] {
  return engineSettings().paymentTerms.map((term) => {
    const labelKey = PAYMENT_TERM_LABEL_KEY[term.code as PaymentTermCode];
    if (!labelKey) throw new Error(`Payment term "${term.code}" of the settings is not allowed by the database check constraint.`);
    return { code: term.code as PaymentTermCode, labelKey, deposit: term.deposit };
  });
}

/**
 * Payment methods printed on the Sellsy estimate (`payment_method_ids`). Labels match Sellsy's
 * `/payments/methods` catalogue (resolved by label at publish time).
 */
export type PaymentMethodCode = "CHEQUE" | "VIREMENT" | "CB" | "PRELEVEMENT";

/** i18n key of each payment-method label. */
export const PAYMENT_METHOD_LABEL_KEY: Readonly<Record<PaymentMethodCode, string>> = {
  CHEQUE: "enginePaymentMethodCheque",
  VIREMENT: "enginePaymentMethodVirement",
  CB: "enginePaymentMethodCb",
  PRELEVEMENT: "enginePaymentMethodPrelevement",
};

/** Payment method of a quote when neither the quote nor the client record chose one. */
export const DEFAULT_PAYMENT_METHOD: PaymentMethodCode = "VIREMENT";

/** Menu order of payment methods (fixed — Sellsy ids are resolved by label, not by this order). */
export const PAYMENT_METHOD_CODES: readonly PaymentMethodCode[] = ["VIREMENT", "CHEQUE", "CB", "PRELEVEMENT"];

/**
 * Payment methods for the menu.
 * @returns Code + label key of each method
 */
export function paymentMethodCodes(): readonly { code: PaymentMethodCode; labelKey: string }[] {
  return PAYMENT_METHOD_CODES.map((code) => ({ code, labelKey: PAYMENT_METHOD_LABEL_KEY[code] }));
}

/** Payment terms, carried by both the client record and the quote header. */
export interface PaymentTerms {
  /** Selected term. Empty until someone chooses, which is what makes the gate possible. */
  paymentDelay?: PaymentTermCode | "";
  /** Selected payment method (Sellsy "moyen de règlement"). Empty until chosen. */
  paymentMethod?: PaymentMethodCode | "";
  /** Does this client require a purchase order number? Reminder only; the number itself lives in Sellsy. */
  poRequired?: boolean;
  /** Accounting contact at the client, all three fields, so an invoice can be chased. */
  billingLastName?: string;
  billingFirstName?: string;
  billingEmail?: string;
}

/**
 * The selected term, or `null` when none is chosen.
 *
 * @param terms The payment terms of a client record or a quote.
 * @returns The matching entry of {@link paymentTermCodes}, or `null`.
 */
export function paymentDelayOf(terms: PaymentTerms | undefined | null): PaymentTermEntry | null {
  const code = terms?.paymentDelay;
  return code ? (paymentTermCodes().find((term) => term.code === code) ?? null) : null;
}

/**
 * i18n key of the client-facing label of the term (the text printed on the quote). `""` when unset.
 *
 * @param terms The payment terms of a client record or a quote.
 * @returns The label key, or an empty string.
 */
export function paymentDelayLabelKey(terms: PaymentTerms | undefined | null): string {
  return paymentDelayOf(terms)?.labelKey ?? "";
}

/**
 * Is money due at order time? True for four of the six terms; the risk is producing before being paid.
 *
 * @param terms The payment terms of a client record or a quote.
 * @returns True when a deposit is due at order time.
 */
export function isDepositDueAtOrder(terms: PaymentTerms | undefined | null): boolean {
  return paymentDelayOf(terms)?.deposit ?? false;
}

/** i18n key of the deposit alert, shared so the input screen and the production table show it identically. */
export const DEPOSIT_ALERT_KEY = "engineDepositAlert";

/**
 * Is the value a non-blank string?
 *
 * @param v The value to test.
 * @returns True for a string with at least one non-whitespace character.
 */
const isFilledString = (v: unknown) => typeof v === "string" && v.trim() !== "";

/** A named missing field: `field` serves tests and the UI, `messageKey` is the i18n key of what the user reads. */
export interface MissingPaymentTerm {
  field: "paymentDelay" | "paymentMethod" | "billingLastName" | "billingFirstName" | "billingEmail";
  messageKey: string;
}

/**
 * The selected payment method, or `null` when none / unknown. The default ({@link DEFAULT_PAYMENT_METHOD}) is written
 * into the quote by the editor, never assumed here.
 * @param terms The payment terms of a client record or a quote
 * @returns The code, or `null`
 */
export function paymentMethodOf(terms: PaymentTerms | undefined | null): PaymentMethodCode | null {
  const code = terms?.paymentMethod;
  return code && (PAYMENT_METHOD_CODES as readonly string[]).includes(code) ? (code as PaymentMethodCode) : null;
}

/**
 * What is missing before a quote can be frozen: the payment delay and the payment method. The accounting contact
 * (name, first name, e-mail) no longer blocks the freeze. The PO checkbox is excluded on purpose: a checkbox always
 * holds an answer. Results follow screen order so a human fixes top to bottom.
 *
 * @param terms The payment terms of a quote.
 * @returns The missing fields, empty when the terms are complete.
 */
export function missingPaymentTerms(
  terms: PaymentTerms | undefined | null,
): MissingPaymentTerm[] {
  const out: MissingPaymentTerm[] = [];
  if (!paymentDelayOf(terms)) out.push({ field: "paymentDelay", messageKey: "engineMissingPaymentDelay" });
  if (!paymentMethodOf(terms)) out.push({ field: "paymentMethod", messageKey: "engineMissingPaymentMethod" });
  return out;
}

/**
 * Convenience check: are the terms complete?
 *
 * @param terms The payment terms of a quote.
 * @returns True when nothing is missing.
 */
export function arePaymentTermsComplete(terms: PaymentTerms | undefined | null): boolean {
  return missingPaymentTerms(terms).length === 0;
}

/**
 * Copies the client record's terms onto a quote that has none yet; never overwrites a value already set.
 * The payment method falls back to {@link DEFAULT_PAYMENT_METHOD} (bank transfer) when nobody chose one.
 *
 * @param clientRecord The terms stored on the client record.
 * @param quoteTerms The terms already set on the quote.
 * @returns The merged terms, quote values taking priority.
 */
export function carryOverFromClient(
  clientRecord: PaymentTerms | undefined | null,
  quoteTerms: PaymentTerms | undefined | null,
): PaymentTerms {
  const quoteSide = quoteTerms ?? {};
  const clientSide = clientRecord ?? {};
  return {
    paymentDelay: quoteSide.paymentDelay || clientSide.paymentDelay || "",
    paymentMethod: quoteSide.paymentMethod || clientSide.paymentMethod || DEFAULT_PAYMENT_METHOD,
    poRequired: quoteSide.poRequired ?? clientSide.poRequired ?? false,
    billingLastName: (isFilledString(quoteSide.billingLastName) ? quoteSide.billingLastName : clientSide.billingLastName) ?? "",
    billingFirstName: (isFilledString(quoteSide.billingFirstName) ? quoteSide.billingFirstName : clientSide.billingFirstName) ?? "",
    billingEmail: (isFilledString(quoteSide.billingEmail) ? quoteSide.billingEmail : clientSide.billingEmail) ?? "",
  };
}

/**
 * Does the quote diverge from the client record? Surfaced as an accepted human edit.
 *
 * @param clientRecord The terms stored on the client record.
 * @param quoteTerms The terms set on the quote.
 * @returns True when any field differs.
 */
export function differsFromClientRecord(
  clientRecord: PaymentTerms | undefined | null,
  quoteTerms: PaymentTerms | undefined | null,
): boolean {
  if (!clientRecord) return false;
  /**
   * Trims strings so whitespace-only differences are ignored.
   *
   * @param v The value to normalise.
   * @returns The trimmed string, or the value unchanged.
   */
  const norm = (v: unknown) => (typeof v === "string" ? v.trim() : v);
  return (
    (quoteTerms?.paymentDelay || "") !== (clientRecord.paymentDelay || "") ||
    (quoteTerms?.paymentMethod || DEFAULT_PAYMENT_METHOD) !== (clientRecord.paymentMethod || DEFAULT_PAYMENT_METHOD) ||
    (quoteTerms?.poRequired ?? false) !== (clientRecord.poRequired ?? false) ||
    norm(quoteTerms?.billingLastName ?? "") !== norm(clientRecord.billingLastName ?? "") ||
    norm(quoteTerms?.billingFirstName ?? "") !== norm(clientRecord.billingFirstName ?? "") ||
    norm(quoteTerms?.billingEmail ?? "") !== norm(clientRecord.billingEmail ?? "")
  );
}
